import axios from 'axios';

export const API_URL = process.env.REACT_APP_API_URL || 'https://apilibrary.zerlak.com';

// CSRF Token Management
let csrfToken = null;

const CSRF_IDENTITY_HEADERS = ['X-Tenant-ID', 'X-User-Email', 'X-User-Role', 'Authorization'];

const readHeaderValue = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') {
    const value = headers.get(name);
    if (value !== null && value !== undefined) return value;
  }
  return headers[name] ?? headers[name.toLowerCase()];
};

const getCsrfIdentityHeaders = (config) => {
  const headers = {};
  CSRF_IDENTITY_HEADERS.forEach((name) => {
    const value = readHeaderValue(config?.headers, name);
    if (value) headers[name] = value;
  });
  return headers;
};

export const fetchCsrfToken = async (identityHeaders = {}) => {
  try {
    const response = await axios.get(`${API_URL}/api/csrf-token`, {
      headers: identityHeaders,
    });
    csrfToken = response.data.csrf_token;
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
};

export const getCsrfToken = () => csrfToken;

const CSRF_METHODS = ['POST', 'PUT', 'DELETE', 'PATCH'];

// CSRF tokens are single-use on the backend, so every state-changing request
// must send a freshly issued token. Cache the token for callers that need it,
// but attach the returned token locally to avoid races between concurrent calls.
export const attachCsrfToken = async (config) => {
  if (!CSRF_METHODS.includes(config.method?.toUpperCase())) return config;
  const token = await fetchCsrfToken(getCsrfIdentityHeaders(config));
  if (token) {
    config.headers['X-CSRF-Token'] = token;
  }
  return config;
};

export const getTenantSessionScope = () => {
  const readJson = (...keys) => {
    for (const key of keys) {
      try {
        const value = localStorage.getItem(key);
        if (value) return JSON.parse(value);
      } catch {
        return {};
      }
    }
    return {};
  };
  const tenantInfo = readJson('tenantinfo');
  const userInfo = readJson('userinfo', 'userInfo');
  const tenantId = tenantInfo.tenant_id || tenantInfo.id || tenantInfo.tenantId ||
    localStorage.getItem('tenant-subdomain') || localStorage.getItem('tenantsubdomain') ||
    localStorage.getItem('tenantId') || 'no-tenant';
  const userId = userInfo.user_id || userInfo.id || userInfo.email || userInfo.username || userInfo.user_name || 'no-user';
  const role = userInfo.role || userInfo.user_type || localStorage.getItem('userRole') || '';
  return `${tenantId}:${userId}:${role}`;
};

export const clearTenantSession = () => {
  // Clear only tenant authentication state. Do not touch
  // SuperAdmin credentials or unrelated application preferences.
  // Note: Token is now stored in HttpOnly cookie, not localStorage
  const keys = [
    'userinfo',
    'userInfo',
    'tenantinfo',
    'tenant-subdomain',
    'tenantsubdomain',
    'tenantId',
    'selectedLibrary',
    'selectedLibraryId',
    'selectedLibraryScope',
    'payment_qr',
    'srms-preview-libraries',
    'userRole',
    'user_type',
    'userName',
    'userEmail',
    'email',
    'adminlogged',
    'admin_logged',
  ];

  keys.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

// Custom modal function for account deactivation
function showAccountDeactivatedModal(message, userType = 'user') {
  // Remove any existing modal
  const existingModal = document.getElementById('account-deactivated-modal');
  if (existingModal) {
    existingModal.remove();
  }

  // Determine modal content based on user type
  const isAdmin = userType === 'admin';
  const modalIcon = isAdmin 
    ? `<svg width="64" height="64" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="11" width="18" height="10" rx="2" stroke="#dc2626" stroke-width="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#dc2626" stroke-width="2" stroke-linecap="round"/>
        <circle cx="12" cy="16" r="1" fill="#dc2626"/>
       </svg>`
    : `<svg width="64" height="64" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="#ef4444" stroke-width="2"/>
        <path d="M12 8v4m0 4h.01" stroke="#ef4444" stroke-width="2" stroke-linecap="round"/>
       </svg>`;

  const modalTitle = isAdmin ? 'Admin Account Deactivated' : 'Account Deactivated';
  const modalMessage = isAdmin 
    ? `${message}<br><br><strong>What happened:</strong> Your administrator access has been revoked by the tenant owner.<br><strong>Next steps:</strong> Please contact your tenant owner to restore your access.`
    : message;

  // Create modal HTML
  const modalHTML = `
    <div id="account-deactivated-modal" class="account-deactivated-modal">
      <div class="modal-overlay"></div>
      <div class="modal-content ${isAdmin ? 'admin-deactivated' : ''}">
        <div class="modal-icon">
          ${modalIcon}
        </div>
        <h2 class="modal-title">${modalTitle}</h2>
        <div class="modal-message">${modalMessage}</div>
        <div class="modal-footer">
          <div class="countdown-text">Redirecting to login in <span id="countdown">5</span> seconds...</div>
          <button class="logout-now-btn" onclick="handleForceLogout()">Logout Now</button>
        </div>
      </div>
    </div>
  `;

  // Add modal to page
  document.body.insertAdjacentHTML('beforeend', modalHTML);
  
  // Debug: Verify modal was added and force visibility
  const modalElement = document.getElementById('account-deactivated-modal');
  if (modalElement) {
    console.log('✅ Modal successfully added to DOM');
    console.log('🎨 Modal HTML:', modalHTML.substring(0, 200) + '...');
    
    // Force modal to be visible with inline styles
    modalElement.style.cssText = `
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      z-index: 999999 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      background: rgba(0, 0, 0, 0.9) !important;
      visibility: visible !important;
      opacity: 1 !important;
    `;
    
    // Also ensure the modal content is visible
    const modalContent = modalElement.querySelector('.modal-content');
    if (modalContent) {
      modalContent.style.visibility = 'visible !important';
      modalContent.style.opacity = '1 !important';
      modalContent.style.display = 'block !important';
    }
    
    console.log('🔧 Applied inline styles to ensure visibility');
  } else {
    console.error('❌ Failed to add modal to DOM');
    console.log('🔍 Document body:', document.body);
    console.log('🔍 Modal HTML length:', modalHTML.length);
  }

  // Add countdown functionality (increased to 5 seconds)
  let countdown = 5;
  const countdownElement = document.getElementById('countdown');
  let countdownInterval = setInterval(() => {
    countdown--;
    if (countdownElement) {
      countdownElement.textContent = countdown;
    }
    if (countdown <= 0) {
      clearInterval(countdownInterval);
      window.location.href = '/tenant-login';
    }
  }, 1000);

  // Add global function for immediate logout
  window.handleForceLogout = () => {
    clearInterval(countdownInterval);
    window.location.href = '/tenant-login';
  };

  // Add styles if not already added
  if (!document.getElementById('account-deactivated-styles')) {
    const styles = `
      .account-deactivated-modal {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        z-index: 99999 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
        background: rgba(0, 0, 0, 0.8) !important;
      }

      .modal-overlay {
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        height: 100% !important;
        background: rgba(0, 0, 0, 0.7) !important;
        backdrop-filter: blur(8px) !important;
      }

      .modal-content {
        position: relative !important;
        background: white !important;
        border-radius: 16px !important;
        padding: 40px !important;
        max-width: 450px !important;
        width: 90% !important;
        max-height: 90vh !important;
        overflow-y: auto !important;
        text-align: center !important;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.5) !important;
        animation: modalSlideIn 0.4s ease-out !important;
        border: 2px solid #e5e7eb !important;
      }

      @keyframes modalSlideIn {
        from {
          opacity: 0;
          transform: scale(0.9) translateY(-20px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }

      .modal-icon {
        margin-bottom: 16px;
        display: flex;
        justify-content: center;
      }

      .modal-title {
        color: #1f2937;
        font-size: 24px;
        font-weight: 600;
        margin: 0 0 16px 0;
      }

      .modal-message {
        color: #6b7280;
        font-size: 16px;
        line-height: 1.5;
        margin: 0 0 24px 0;
      }

      .modal-footer {
        border-top: 1px solid #e5e7eb;
        padding-top: 16px;
      }

      .countdown-text {
        color: #059669;
        font-size: 14px;
        font-weight: 500;
      }

      .countdown-text span {
        font-weight: 700;
        color: #dc2626;
      }

      .logout-now-btn {
        background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        margin-top: 12px;
        transition: all 0.2s ease;
        box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
      }

      .logout-now-btn:hover {
        background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(220, 38, 38, 0.4);
      }

      .logout-now-btn:active {
        transform: translateY(0);
      }

      /* Admin-specific styling */
      .modal-content.admin-deactivated {
        border-top: 4px solid #dc2626;
      }

      .modal-content.admin-deactivated .modal-title {
        color: #dc2626;
      }

      .modal-content.admin-deactivated .modal-message {
        color: #374151;
      }

      .modal-content.admin-deactivated .modal-message strong {
        color: #1f2937;
        display: block;
        margin-top: 8px;
      }
    `;

    const styleElement = document.createElement('style');
    styleElement.id = 'account-deactivated-styles';
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
  }
}

// ─── Superadmin Axios Instance ────────────────────────────────────────────────
// Superadmin ला tenant context नसतो — स्वतंत्र instance वापरतो
export const superadminApi = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

superadminApi.interceptors.request.use(async (config) => {
  // Superadmin token / email लावतो
  const superadminUser = JSON.parse(localStorage.getItem('superadmin_user') || '{}');
  if (superadminUser?.email) {
    config.headers['X-User-Email'] = superadminUser.email;
  }
  // Superadmin role लावतो - hardcoded since all superadminApi calls are for superadmin
  config.headers['X-User-Role'] = 'superadmin';
  // Token is now sent via HttpOnly cookie, no Authorization header needed
  // Add a fresh CSRF token for state-changing requests (tokens are single-use)
  await attachCsrfToken(config);
  return config;
}, (error) => Promise.reject(error));

superadminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('superadmin_user');
      window.location.href = '/superadmin/login';
    }
    return Promise.reject(error);
  }
);
// ─────────────────────────────────────────────────────────────────────────────

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ✅ Add request interceptor to include tenant header, user email AND role
api.interceptors.request.use(
  async (config) => {
    // Skip tenant context for auth endpoints
    const skipTenantContext = ['/api/login', '/api/auth/check'].includes(config.url);

    if (!skipTenantContext) {
      // Get tenant info from multiple sources
      const tenantInfo = JSON.parse(localStorage.getItem('tenantinfo') || '{}');
      const tenantSubdomain = localStorage.getItem('tenant-subdomain');
      const tenantId = tenantInfo.tenant_id || tenantInfo.id || tenantInfo.tenantId;

      // Set X-Tenant-ID header if we have tenant info
      if (tenantId) {
        config.headers['X-Tenant-ID'] = tenantId;
        console.log(`🔑 Request to ${config.url} with Tenant ID: ${tenantId}`);
      } else if (tenantSubdomain) {
        config.headers['X-Tenant-ID'] = tenantSubdomain;
        console.log(`🔑 Request to ${config.url} with Tenant Subdomain: ${tenantSubdomain}`);
      } else {
        console.warn(`⚠️ Request to ${config.url} WITHOUT tenant context!`);
      }
    }

    // ✅ Add user email AND role to ALL requests (except auth endpoints)
    if (!skipTenantContext) {
      try {
        const userInfoStr =
          localStorage.getItem('userinfo') || localStorage.getItem('userInfo');
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr);

          // Add email header
          if (userInfo.email) {
            config.headers['X-User-Email'] = userInfo.email;
            console.log(`👤 User Email: ${userInfo.email}`);
          }

          // Add role header (critical for permission checks)
          if (userInfo.role) {
            config.headers['X-User-Role'] = userInfo.role;
            console.log(`🎭 User Role: ${userInfo.role}`);
          }
        } else {
          console.warn('⚠️ No user info found in localStorage');
        }
      } catch (e) {
        console.error('❌ Failed to parse userinfo:', e);
        // Clear corrupted data
        localStorage.removeItem('userinfo');
        localStorage.removeItem('userInfo');
      }
    }

    // Add a fresh CSRF token for state-changing requests (tokens are single-use)
    await attachCsrfToken(config);

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ✅ Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // ✅ Handle Network/CORS Errors
    if (error.code === 'ERR_NETWORK') {
      console.error('❌ Network Error: Likely CORS policy violation or Backend is unreachable. Check backend logs.');
    }

    // Handle 403 errors (tenant context missing or permission denied)
    if (error.response?.status === 403) {
      const isLoginRequest = error.config?.url?.includes('/api/login');

      // Check for forced logout due to deactivation
      if (error.response?.data?.force_logout && !isLoginRequest) {
        const errorData = error.response.data;
        console.warn('🔒 User account deactivated - forcing logout:', errorData.message);
        
        // Check if user is admin based on stored user info BEFORE clearing localStorage
        const userInfoStr = localStorage.getItem('userinfo');
        let userType = 'user';
        if (userInfoStr) {
          try {
            const userInfo = JSON.parse(userInfoStr);
            userType = userInfo.role === 'admin' ? 'admin' : 'user';
            console.log('🔍 Detected user type:', userType);
          } catch (e) {
            console.error('Failed to parse user info:', e);
          }
        }
        
        // Show custom deactivated account modal FIRST (before clearing data)
        console.log('🎨 Showing deactivation modal for:', userType);
        showAccountDeactivatedModal(errorData.message || 'Your account has been deactivated. Please contact your administrator.', userType);
        
        // Clear all session data AFTER showing modal
        localStorage.removeItem('adminlogged');
        localStorage.removeItem('admin_logged');
        localStorage.removeItem('userinfo');
        localStorage.removeItem('tenantinfo');
        localStorage.removeItem('tenant-subdomain');
        localStorage.removeItem('userRole');
        
        // Redirect to login after a delay (increased for admin to read message)
        const redirectDelay = userType === 'admin' ? 5000 : 3000;
        setTimeout(() => {
          window.location.href = '/tenant-login';
        }, redirectDelay);
        
        return new Promise(() => {}); // Halt promise chain to prevent UI errors before redirect
      }
      
      // Check for inactive/deleted tenant specifically
      if (error.response?.data?.error === 'Invalid or inactive tenant') {
        console.warn('🚫 Tenant is inactive or deleted. Forcing logout.');
        localStorage.removeItem('adminlogged');
        localStorage.removeItem('admin_logged');
        localStorage.removeItem('userinfo');
        localStorage.removeItem('tenantinfo');
        localStorage.removeItem('tenant-subdomain');
        window.location.href = '/tenant-login';
        return new Promise(() => {}); // Halt promise chain to prevent UI errors before redirect
      }

      console.error('❌ 403 Forbidden - Check tenant context or permissions!');
      console.log('Stored tenant info:', localStorage.getItem('tenantinfo'));
      console.log('Stored user info:', localStorage.getItem('userinfo'));
      console.log('Stored subdomain:', localStorage.getItem('tenant-subdomain'));

      // alert(`Permission Denied: ${errorMsg}`); // Replaced by component-level popups
    }

    // Handle 401 errors.
    // Do NOT clear persisted login state for every 401: some endpoints can
    // return 401 during startup/refresh for reasons unrelated to tenant
    // deactivation. Only an explicit force_logout response is authoritative.
    if (error.response?.status === 401) {
      console.warn('⚠️ 401 Unauthorized:', error.response?.data || error.message);
    }

    // Handle 400 errors (bad request - usually validation)
    if (error.response?.status === 400) {
      console.error('❌ 400 Bad Request:', error.response?.data);
      // alert(`Error: ${errorMsg}`); // Replaced by component-level popups
    }

    return Promise.reject(error);
  }
);

export default api;

// ---------------------------------------------------------------------------
// Global GET request deduplication/cache
// ---------------------------------------------------------------------------
// Multiple persistent layout components (Navbar, LibraryContext, Dashboard,
// Reports, etc.) can ask for the same resource while a route is mounting.
// Keep this at the axios-instance level so callers using `api.get()` and the
// higher-level service helpers share the same in-flight request/cache.
const originalApiGet = api.get.bind(api);
const originalApiPost = api.post.bind(api);
const originalApiPut = api.put.bind(api);
const originalApiPatch = api.patch.bind(api);
const originalApiDelete = api.delete.bind(api);

const globalGetInFlight = new Map();
const globalGetCache = new Map();
let globalGetCacheGeneration = 0;

const GET_CACHE_TTL = {
  '/api/auth/check': 5000,
  '/api/libraries': 300000,
  '/api/payment-plans': 30000,
  '/api/notifications': 30000,
  '/api/notifications/plans': 30000,
  '/api/notifications/unread-count': 10000,
  '/api/seats': 15000,
  '/api/rooms': 60000,
  '/api/students': 15000,
  '/api/students/summary': 15000,
  '/api/payments': 15000,
  '/api/dashboard': 15000,
  '/api/dashboard/complete': 15000,
};

const normalizeGetParams = (params = {}) => Object.keys(params)
  .filter(key => params[key] !== undefined && params[key] !== null)
  .sort()
  .map(key => `${key}=${String(params[key])}`)
  .join('&');

const getCacheKey = (url, config = {}) => {
  return `GET:${getTenantSessionScope()}:${url}?${normalizeGetParams(config.params)}`;
};

const getTtl = (url) => {
  const cleanUrl = String(url || '').split('?')[0];
  return GET_CACHE_TTL[cleanUrl] || 0;
};

const clearGlobalGetCache = () => {
  globalGetCacheGeneration += 1;
  globalGetCache.clear();
  requestCacheGeneration += 1;
  requestCache.clear();
};

export const clearApiDataCache = () => {
  clearGlobalGetCache();
  globalGetInFlight.clear();
  inFlightRequests.clear();
};

api.get = (url, config = {}) => {
  // Reports/exports and any explicitly skipped request must remain fresh.
  const skipCache = Boolean(config.skipCache);
  const cleanConfig = { ...config };
  delete cleanConfig.skipCache;

  const ttl = skipCache ? 0 : getTtl(url);
  const key = getCacheKey(url, cleanConfig);

  if (ttl > 0) {
    const cached = globalGetCache.get(key);
    if (cached && Date.now() - cached.timestamp < ttl) {
      return Promise.resolve(cached.response);
    }
  }

  if (globalGetInFlight.has(key)) {
    return globalGetInFlight.get(key);
  }

  const cacheGeneration = globalGetCacheGeneration;
  const request = originalApiGet(url, cleanConfig)
    .then(response => {
      if (ttl > 0 && cacheGeneration === globalGetCacheGeneration) {
        globalGetCache.set(key, { timestamp: Date.now(), response });
      }
      return response;
    })
    .finally(() => {
      if (globalGetInFlight.get(key) === request) globalGetInFlight.delete(key);
    });

  globalGetInFlight.set(key, request);
  return request;
};

// Any successful write can make cached GET data stale. Clear the shared cache
// after the write so the next screen fetch gets fresh data.
api.post = (...args) => originalApiPost(...args).then(res => { clearGlobalGetCache(); return res; });
api.put = (...args) => originalApiPut(...args).then(res => { clearGlobalGetCache(); return res; });
api.patch = (...args) => originalApiPatch(...args).then(res => { clearGlobalGetCache(); return res; });
api.delete = (...args) => originalApiDelete(...args).then(res => { clearGlobalGetCache(); return res; });

const inFlightRequests = new Map();
const requestCache = new Map();
let requestCacheGeneration = 0;

async function deduplicateRequest(key, requestFn, cacheDuration = 0) {
  const scopedKey = `${getTenantSessionScope()}:${key}`;
  const cacheGeneration = requestCacheGeneration;
  if (cacheDuration > 0 && cacheGeneration === requestCacheGeneration) {
    const cached = requestCache.get(scopedKey);
    if (cached && (Date.now() - cached.timestamp < cacheDuration)) {
      return cached.data;
    }
  }

  if (inFlightRequests.has(scopedKey)) {
    return inFlightRequests.get(scopedKey);
  }

  const promise = requestFn().then(data => {
    if (cacheDuration > 0) {
      requestCache.set(scopedKey, { timestamp: Date.now(), data });
    }
    return data;
  });

  inFlightRequests.set(scopedKey, promise);

  promise.finally(() => {
    if (inFlightRequests.get(scopedKey) === promise) inFlightRequests.delete(scopedKey);
  });

  return promise;
}

// ✅ Helper to deduplicate raw API calls based on Endpoint + Params
// This ensures that if getDashboardData and getSeatsData both call /api/seats, they share one request.
const fetchApi = (endpoint, params = {}, cacheDuration = 0) => {
  // Create a stable key by sorting param keys
  const queryString = Object.entries(params)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&');
  const key = `API:${getTenantSessionScope()}:${endpoint}?${queryString}`;

  return deduplicateRequest(key, async () => {
    const res = await api.get(endpoint, { params });
    return res.data;
  }, cacheDuration);
};

const resolveLibrary = (selectedLibrary) => {
  if (selectedLibrary) {
    return selectedLibrary;
  }
  return localStorage.getItem('selectedLibraryId') || localStorage.getItem('selectedLibrary') || 'All Libraries';
};

export const getLibraries = async () => {
  return fetchApi('/api/libraries', {}, 300000); // Cache for 5 minutes
};

export const getDashboardData = async (selectedLibrary, cacheBust = null) => {
  const lib = resolveLibrary(selectedLibrary);
  const key = cacheBust ? `getDashboardData-${lib}-${cacheBust}` : `getDashboardData-${lib}`;
  return deduplicateRequest(key, async () => {
    try {
      const params = {};
      if (lib && lib !== 'All Libraries') {
        params.library_id = lib;
      }
      // ✅ OPTIMIZATION: Run requests in parallel to reduce loading time
      const [seatRes, paymentsRes, dashboardRes] = await Promise.all([
        fetchApi('/api/seats', params, 30000).catch(() => []),
        fetchApi('/api/payments', params, 60000).catch(() => []),
        fetchApi('/api/dashboard', params, cacheBust ? 0 : 60000).catch(() => ({}))
      ]);
      return {
        seats: seatRes,
        payments: paymentsRes,
        dashboard: dashboardRes,
      };
    } catch (error) {
      console.error("Failed to load dashboard data", error);
      throw error;
    }
  }, cacheBust ? 0 : 60000); // Cache for 60 seconds, or 0 if cache-busting
};

export const getSeatsData = async (selectedLibrary) => {
  const lib = resolveLibrary(selectedLibrary);
  const key = `getSeatsData-${lib}`;
  return deduplicateRequest(key, async () => {
    try {
        const params = {};
        if (lib && lib !== 'All Libraries') {
            params.library_id = lib;
        }
        // ✅ OPTIMIZATION: Run requests in parallel
        const [seatsRes, roomsRes] = await Promise.all([
            fetchApi('/api/seats', params, 30000),
            fetchApi('/api/rooms', params, 300000)
        ]);
        
        return {
            seats: seatsRes || [],
            rooms: roomsRes || [],
        };
    } catch (error) {
        console.error('Failed to load seats:', error);
        throw error;
    }
  }, 30000); // Cache for 30 seconds
};

export const getNotifications = async () => {
  const response = await api.get('/api/notifications');
  const payload = response.data;
  if (Array.isArray(payload?.notifications)) return payload.notifications;
  if (Array.isArray(payload?.data?.notifications)) return payload.data.notifications;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

export const getUnreadNotificationCount = async () => {
  try {
    const res = await api.get('/api/notifications/unread-count');
    return res.data?.unread_count || 0;
  } catch (err) {
    console.error("Error fetching unread notification count:", err);
    return 0;
  }
};

export const markNotificationAsRead = async (notificationId) => {
  try {
    const res = await api.put(`/api/notifications/${notificationId}/read`);
    return res.data;
  } catch (error) {
    console.error('❌ Failed to mark notification as read:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const markAllNotificationsAsRead = async () => {
  try {
    const res = await api.put('/api/notifications/mark-all-read');
    return res.data;
  } catch (error) {
    console.error('❌ Failed to mark all notifications as read:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const getPayments = async (selectedLibrary) => {
  const lib = resolveLibrary(selectedLibrary);
  const params = {};
  if (lib && lib !== 'All Libraries') {
      params.library_id = lib;
  }
  console.log('🔍 Fetching payments with params:', params, 'selectedLibrary:', selectedLibrary);
  return fetchApi('/api/payments', params, 60000);
};

export const getStudents = async (selectedLibrary) => {
  const lib = resolveLibrary(selectedLibrary);
  const params = {};
  if (lib && lib !== 'All Libraries') {
      params.library_id = lib;
  }
  return fetchApi('/api/students', params, 30000);
};

export const getStudentPaymentPlans = async (studentId) => {
    // For functions with parameters, the key needs to be dynamic
    const key = `getStudentPaymentPlans-${studentId}`;
    return deduplicateRequest(key, async () => {
      try {
          const res = await api.get(`/api/students/${studentId}/payment-plans`);
          return res.data || [];
      } catch (err) {
          console.warn("Specific plan fetch failed, trying fallback...", err);
          
          const lib = resolveLibrary();
          const params = {};
          if (lib && lib !== 'All Libraries') {
            params.library_id = lib;
          }

          // Fallback to all plans with filter
          return fetchApi('/api/payment-plans', params, 60000);
      }
    });
};

export const getStudentOutstanding = async (studentId) => {
    const key = `getStudentOutstanding-${studentId}`;
    return deduplicateRequest(key, async () => {
      try {
          const res = await api.get(`/api/students/${studentId}/outstanding`);
          return res.data || null;
      } catch (err) {
          console.warn('Failed to fetch outstanding info', err);
          return null;
      }
    });
};

export const createPayment = async (payload) => {
    // POST requests are generally not deduplicated as they are not idempotent
    try {
        const res = await api.post(`/api/payments`, payload);
        return res.data;
    } catch (error) {
        console.error('❌ Payment failed:', error.response?.data || error);
        throw error.response?.data || error;
    }
};

export const updateStudent = async (studentId, payload) => {
    // PUT requests are generally not deduplicated as they might not be idempotent in some contexts
    try {
        const res = await api.put(`/api/students/${studentId}`, payload);
        return res.data;
    } catch (error) {
        console.error('❌ Student update failed:', error.response?.data || error);
        throw error.response?.data || error;
    }
};

export const assignSeat = async (studentId, seatId) => {
    // POST requests are generally not deduplicated
    try {
        const res = await api.post(`/api/students/${studentId}/assign-seat-manual`, { seatid: seatId });
        return res.data;
    } catch (error) {
        console.error('❌ Seat assignment failed:', error.response?.data || error);
        throw error.response?.data || error;
    }
};

export const getReport = async (reportType, params = {}) => {
    const lib = resolveLibrary();
    const queryParams = { ...params };
    if (lib && lib !== 'All Libraries') {
        queryParams.library_id = lib;
    }
    const key = `getReport-${reportType}-${JSON.stringify(queryParams)}`; 
    return deduplicateRequest(key, async () => {
      try {
          const response = await api.get(`/api/reports/${reportType}`, { params: queryParams });
          return response.data;
      } catch (err) {
          console.error(`Failed to load report ${reportType}`, err);
          throw err.response?.data || err;
      }
    });
};

export const exportReport = async (reportType, params = {}) => {
    const lib = resolveLibrary();
    const queryParams = { ...params };
    if (lib && lib !== 'All Libraries') {
        queryParams.library_id = lib;
    }
    // Exporting a report should generally not be deduplicated to allow multiple downloads
    try {
        const response = await api.get(`/api/reports/${reportType}/export`, {
            params: queryParams,
            responseType: 'blob'
        });
        return response.data;
    } catch (err) {
        console.error(`Failed to export report ${reportType}`, err);
        throw err.response?.data || err;
    }
};

export const createSeat = async (payload) => {
  // POST requests are generally not deduplicated
  try {
    const res = await api.post(`/api/seats`, payload);
    return res.data;
  } catch (error) {
    console.error('❌ Seat creation failed:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const deleteSeat = async (seatId) => {
  try {
    const res = await api.delete(`/api/seats/${seatId}`);
    return res.data;
  } catch (error) {
    console.error('❌ Seat deletion failed:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const sendPlanAlert = async (studentId) => {
  // POST requests are generally not deduplicated
  try {
    const res = await api.post(`/api/students/${studentId}/send-plan-alert`);
    return res.data;
  } catch (error) {
    console.error('❌ Failed to send plan alert:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const sendWhatsappPlanAlert = async (studentId) => {
  // POST requests are generally not deduplicated
  try {
    const res = await api.post(`/api/students/${studentId}/send-whatsapp-plan-alert`);
    return res.data;
  } catch (error) {
    console.error('❌ Failed to send WhatsApp plan alert:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const deleteStudent = async (studentId) => {
    // DELETE requests are generally not deduplicated
    try {
        const res = await api.delete(`/api/students/${studentId}`);
        return res.data;
    } catch (error) {
        console.error('❌ Student deletion failed:', error.response?.data || error);
        throw error.response?.data || error;
    }
};

export const getStudentsWithOutstanding = async (selectedLibrary) => {
  const lib = resolveLibrary(selectedLibrary);
  const key = `getStudentsWithOutstanding-${lib}`;
  return deduplicateRequest(key, async () => {
    try {
      const params = {};
      if (lib && lib !== 'All Libraries') {
          params.library_id = lib;
      }
      const res = await api.get('/api/dashboard', { params });
      return res.data?.dashboard?.students_with_due || [];
    } catch (error) {
      console.error('❌ Failed to fetch students with outstanding payments:', error);
      return [];
    }
  }, 60000); // Cache for 60 seconds
};

export const getRooms = async (selectedLibrary) => {
  const lib = resolveLibrary(selectedLibrary);
  const params = {};
  if (lib && lib !== 'All Libraries') {
      params.library_id = lib;
  }
  return fetchApi('/api/rooms', params, 300000);
};

export const getAvailableSeats = async (selectedLibrary, category) => {
  const lib = resolveLibrary(selectedLibrary);
  try {
      const params = {};
      if (lib && lib !== 'All Libraries') {
          params.library_id = lib;
      }
      if (category) {
          params.category = category;
      }
      return fetchApi('/api/seats/available', params, 30000);
  } catch (error) {
      console.error('Failed to load available seats:', error);
      throw error;
  }
};

// ==============================
// WhatsApp API Functions
// ==============================

export const sendWhatsAppMessage = async (phone, message, useTwilio = false) => {
  try {
    const res = await api.post('/api/send-whatsapp', {
      phone,
      message,
      use_twilio: useTwilio
    });
    return res.data;
  } catch (error) {
    console.error('❌ Failed to send WhatsApp message:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const sendWhatsAppTemplate = async (phone, templateName, components = null) => {
  try {
    const res = await api.post('/api/send-template', {
      phone,
      template_name: templateName,
      components
    });
    return res.data;
  } catch (error) {
    console.error('❌ Failed to send WhatsApp template:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const sendBookingConfirmation = async (phone, customerName, bookingId, useTwilio = false) => {
  try {
    const res = await api.post('/api/send-booking-confirmation', {
      phone,
      customer_name: customerName,
      booking_id: bookingId,
      use_twilio: useTwilio
    });
    return res.data;
  } catch (error) {
    console.error('❌ Failed to send booking confirmation:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const sendOTP = async (phone, otpCode, useTwilio = false) => {
  try {
    const res = await api.post('/api/send-otp', {
      phone,
      otp_code: otpCode,
      use_twilio: useTwilio
    });
    return res.data;
  } catch (error) {
    console.error('❌ Failed to send OTP:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const sendPaymentConfirmation = async (phone, customerName, amount, useTwilio = false) => {
  try {
    const res = await api.post('/api/send-payment-confirmation', {
      phone,
      customer_name: customerName,
      amount,
      use_twilio: useTwilio
    });
    return res.data;
  } catch (error) {
    console.error('❌ Failed to send payment confirmation:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const sendPaymentReceiptPdfWhatsApp = async (phone, customerName, paymentId, documentUrl, documentName) => {
  try {
    const res = await api.post('/api/whatsapp/send-payment-receipt-pdf', {
      phone,
      customer_name: customerName,
      payment_id: paymentId,
      document_url: documentUrl,
      document_name: documentName
    });
    return res.data;
  } catch (error) {
    console.error('❌ Failed to send payment receipt PDF via WhatsApp:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const sendStudentPaymentHistoryWhatsApp = async (phone, studentName, paymentHistory, totalPaid, outstanding) => {
  try {
    const res = await api.post('/api/whatsapp/send-student-payment-history', {
      phone,
      student_name: studentName,
      payment_history: paymentHistory,
      total_paid: totalPaid,
      outstanding: outstanding
    });
    return res.data;
  } catch (error) {
    console.error('❌ Failed to send student payment history via WhatsApp:', error.response?.data || error);
    throw error.response?.data || error;
  }
};
